import { Autocomplete, Box, Grid, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CustomerIntakeForm } from '../types/CustomerIntakeForm';

interface OnboardingServiceRequirementsProps {
  formData: CustomerIntakeForm;
  handleTagsChange: (field: keyof CustomerIntakeForm, value: string[]) => void;
  handleChange: (field: keyof CustomerIntakeForm, value: string) => void;
}

const USER_TYPE_OPTIONS = ['Patients', 'Staff', 'Visitors', 'Students', 'Corporate employees', 'General public'];
const SERVICE_TIME_OPTIONS = ['Breakfast', 'Brunch', 'Lunch', 'Afternoon tea', 'Dinner', '24-hour'];
const MENU_OPTIONS = ['Ward trolley', 'Retail restaurant', 'Staff canteen', 'Cafe', 'Vending', 'Hospitality/events'];
const DIETARY_OPTIONS = ['Vegetarian', 'Vegan', 'Halal', 'Kosher', 'Gluten-free', 'Dairy-free', 'Low sodium', 'Texture-modified'];
const ALLERGEN_OPTIONS = ['Nuts', 'Peanuts', 'Gluten', 'Dairy', 'Eggs', 'Soya', 'Fish', 'Shellfish', 'Sesame', 'Sulphites'];

/** freeSolo autocomplete so demo users can either pick a common option or type a bespoke one -
 * keeps the intake flexible without a managed taxonomy behind it (lazy: no backend-driven
 * catalogue yet, upgrade path is to source these lists from a real menu/allergen service). */
const TagField: React.FC<{ label: string; options: string[]; value: string[]; onChange: (value: string[]) => void; required?: boolean }> = ({
  label, options, value, onChange, required,
}) => (
  <Autocomplete
    multiple
    freeSolo
    options={options}
    value={value}
    onChange={(_, newValue) => onChange(newValue as string[])}
    renderInput={(params) => (
      <TextField {...params}
        label={label}
        required={required && value.length === 0} />
    )}
    sx={{ mb: 8 }}
  />
);

const OnboardingServiceRequirements: React.FC<OnboardingServiceRequirementsProps> = ({ formData, handleTagsChange, handleChange }) => {
  const { t } = useTranslation('common');

  return (
    <Box sx={{ p: 6, paddingBottom: 0 }}>
      <Typography variant="textSM"
        color="text.secondary"
        sx={{ mb: 4, display: 'block' }}>
        {t('onboardingIntake.serviceRequirementsHelp', 'Capture who is being served, when, and any dietary or allergen requirements the catering offer must accommodate.')}
      </Typography>
      <Grid container
        spacing={3}>
        <Grid item
          xs={12}
          sx={{ pr: 4 }}
          md={6}>
          <TagField
            label={t('onboardingIntake.userTypes', 'Type of users')}
            options={USER_TYPE_OPTIONS}
            value={formData.userTypes}
            onChange={(value) => handleTagsChange('userTypes', value)}
            required />
          <TagField
            label={t('onboardingIntake.serviceTimes', 'Service times')}
            options={SERVICE_TIME_OPTIONS}
            value={formData.serviceTimes}
            onChange={(value) => handleTagsChange('serviceTimes', value)}
            required />
          <TagField
            label={t('onboardingIntake.menus', 'Menus')}
            options={MENU_OPTIONS}
            value={formData.menus}
            onChange={(value) => handleTagsChange('menus', value)} />
        </Grid>

        <Grid item
          sx={{ pl: 4 }}
          xs={12}
          md={6}>
          <TagField
            label={t('onboardingIntake.dietaryRequirements', 'Dietary requirements')}
            options={DIETARY_OPTIONS}
            value={formData.dietaryRequirements}
            onChange={(value) => handleTagsChange('dietaryRequirements', value)}
            required />
          <TagField
            label={t('onboardingIntake.allergens', 'Allergens to accommodate')}
            options={ALLERGEN_OPTIONS}
            value={formData.allergens}
            onChange={(value) => handleTagsChange('allergens', value)}
            required />
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('onboardingIntake.hospitalityNeeds', 'Hospitality needs')}
            placeholder={t('onboardingIntake.hospitalityNeedsPlaceholder', 'e.g. boardroom catering, event hospitality, VIP requirements')}
            value={formData.hospitalityNeeds}
            onChange={(e) => handleChange('hospitalityNeeds', e.target.value)}
            sx={{ mb: 8 }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default OnboardingServiceRequirements;
